import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, fetchApi } from '@/lib/api';
import { useSession } from '@/lib/auth-client';
import type { ProgressMessage, Review, ReviewMedia } from './types';

const REVIEWS_KEY = ['reviews'] as const;

const MAX_IMAGES = 10;
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

async function fetchReviewsApi<T>(path: string, init?: RequestInit): Promise<T> {
  const url = `/api/reviews${path}`;
  const res = await fetch(url, { ...init, credentials: 'include' });
  const text = await res.text();
  let json: { success?: boolean; data?: T; error?: { message?: string } } | null = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {}
  if (!res.ok || !json?.success) {
    throw new Error(json?.error?.message ?? `Request failed: ${res.status}`);
  }
  return json.data as T;
}

function getFileId(file: File): string {
  return `${file.name}-${file.size}-${file.lastModified}`;
}

function validateFiles(
  files: File[],
  uploadingCount: number,
  uploadedCount: number,
): string | null {
  const totalFiles = files.length + uploadingCount + uploadedCount;
  if (totalFiles > MAX_IMAGES) {
    return `ניתן להעלות עד ${MAX_IMAGES} תמונות`;
  }

  for (const file of files) {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'תמונות חייבות להיות בפורמט JPG, PNG או WebP';
    }

    if (file.size > MAX_FILE_SIZE) {
      return 'תמונות חייבות להיות קטנות מ-5MB';
    }
  }

  return null;
}

export function useReviews(enabled = true) {
  return useInfiniteQuery({
    queryKey: REVIEWS_KEY,
    queryFn: async ({ pageParam }) => {
      const data = await fetchApi(api.api.reviews.get({ query: { page: pageParam, limit: 20 } }));
      return data as { items: Review[]; page: number; hasNextPage: boolean };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.hasNextPage ? lastPage.page + 1 : undefined),
    enabled,
  });
}

export function useReviewImageUpload(itemUin: number) {
  const { data: sessionData } = useSession();
  const user = sessionData?.user ?? null;

  const [uploadedImages, setUploadedImages] = useState<ReviewMedia[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<Map<string, File>>(new Map());
  const [previewUrls, setPreviewUrls] = useState<Map<string, string>>(new Map());
  const [isDragging, setIsDragging] = useState(false);
  const [progressMessages, setProgressMessages] = useState<ProgressMessage[]>([]);

  const addProgressMessage = useCallback((text: string, type: ProgressMessage['type']) => {
    const id = Date.now().toString() + Math.random().toString(36);
    setProgressMessages((prev) => [...prev, { text, type, id }]);

    if (type !== 'error') {
      setTimeout(() => {
        setProgressMessages((prev) => prev.filter((msg) => msg.id !== id));
      }, 3000);
    }
  }, []);

  const removeProgressMessage = useCallback((id: string) => {
    setProgressMessages((prev) => prev.filter((msg) => msg.id !== id));
  }, []);

  const uploadSingleImageImmediately = useCallback(
    async (file: File, fileId: string): Promise<void> => {
      try {
        const { id, uploadUrl, key } = await fetchReviewsApi<{
          id: string;
          uploadUrl: string;
          key: string;
        }>(`/${itemUin}/media`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            filename: file.name,
            contentType: file.type,
            itemUin,
          }),
        });

        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type,
          },
        });

        if (!uploadResponse.ok) {
          throw new Error(`Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
        }

        const now = new Date().toISOString();
        const media: ReviewMedia = {
          id,
          userId: user?.id ?? '',
          reviewId: null,
          itemUin,
          filename: file.name,
          s3Key: key,
          mimeType: file.type,
          size: file.size,
          deletedAt: null,
          createdAt: now,
          updatedAt: now,
        };

        setUploadedImages((prev) => [...prev, media]);
        setUploadingFiles((prev) => {
          const next = new Map(prev);
          next.delete(fileId);
          return next;
        });

        setPreviewUrls((prev) => {
          const next = new Map(prev);
          const previewUrl = next.get(fileId);
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            next.delete(fileId);
          }
          return next;
        });

        addProgressMessage(`הועלה בהצלחה: ${file.name}`, 'success');
      } catch {
        setUploadingFiles((prev) => {
          const next = new Map(prev);
          next.delete(fileId);
          return next;
        });

        setPreviewUrls((prev) => {
          const next = new Map(prev);
          const previewUrl = next.get(fileId);
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            next.delete(fileId);
          }
          return next;
        });

        addProgressMessage(`שגיאה בהעלאת ${file.name}`, 'error');
      }
    },
    [addProgressMessage, itemUin, user?.id],
  );

  const addFiles = useCallback(
    async (files: File[]): Promise<void> => {
      const error = validateFiles(files, uploadingFiles.size, uploadedImages.length);
      if (error) {
        addProgressMessage(error, 'error');
        return;
      }

      const existingFileIds = new Set([
        ...Array.from(uploadingFiles.keys()),
        ...uploadedImages.map((image) => image.filename),
      ]);

      const newFiles = files.filter((file) => {
        const fileId = getFileId(file);
        return !existingFileIds.has(fileId) && !existingFileIds.has(file.name);
      });

      if (newFiles.length === 0) {
        addProgressMessage('קבצים אלה כבר נבחרו או הועלו', 'error');
        return;
      }

      for (const file of newFiles) {
        const fileId = getFileId(file);
        setUploadingFiles((prev) => new Map(prev).set(fileId, file));
        const previewUrl = URL.createObjectURL(file);
        setPreviewUrls((prev) => new Map(prev).set(fileId, previewUrl));
        uploadSingleImageImmediately(file, fileId);
      }

      addProgressMessage(`מתחיל להעלות ${newFiles.length} תמונות`, 'info');
    },
    [addProgressMessage, uploadedImages, uploadingFiles, uploadSingleImageImmediately],
  );

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) {
        return;
      }
      addFiles(Array.from(files));
      event.target.value = '';
    },
    [addFiles],
  );

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX;
    const y = event.clientY;

    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      setIsDragging(false);

      const files = event.dataTransfer?.files;
      if (!files || files.length === 0) {
        return;
      }

      const imageFiles = Array.from(files).filter((file) => ALLOWED_TYPES.includes(file.type));
      if (imageFiles.length === 0) {
        addProgressMessage('אנא גררו רק קבצי תמונה (JPG, PNG, WebP)', 'error');
        return;
      }

      addFiles(imageFiles);
    },
    [addFiles, addProgressMessage],
  );

  const handleDeleteUploadedImage = useCallback(async (mediaId: string) => {
    try {
      await fetchApi(api.api.reviews.media({ id: mediaId }).delete());
      setUploadedImages((prev) => prev.filter((img) => img.id !== mediaId));
      toast.success('התמונה נמחקה בהצלחה');
    } catch {
      toast.error('שגיאה במחיקת התמונה');
      throw new Error('Failed to delete image');
    }
  }, []);

  const handleRemoveUploadingFile = useCallback((fileId: string) => {
    setUploadingFiles((prev) => {
      const next = new Map(prev);
      next.delete(fileId);
      return next;
    });

    setPreviewUrls((prev) => {
      const next = new Map(prev);
      const previewUrl = next.get(fileId);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        next.delete(fileId);
      }
      return next;
    });
  }, []);

  const initForEdit = useCallback((media: ReviewMedia[]) => {
    setUploadedImages([...media]);
    setUploadingFiles(new Map());
    setPreviewUrls((prev) => {
      for (const url of prev.values()) {
        URL.revokeObjectURL(url);
      }
      return new Map();
    });
    setIsDragging(false);
    setProgressMessages([]);
  }, []);

  const reset = useCallback(() => {
    setUploadedImages([]);
    setUploadingFiles(new Map());
    setPreviewUrls((prev) => {
      for (const url of prev.values()) {
        URL.revokeObjectURL(url);
      }
      return new Map();
    });
    setIsDragging(false);
    setProgressMessages([]);
  }, []);

  useEffect(() => {
    return () => {
      for (const url of previewUrls.values()) {
        URL.revokeObjectURL(url);
      }
    };
  }, [previewUrls]);

  return {
    uploadedImages,
    setUploadedImages,
    uploadingFiles,
    previewUrls,
    isDragging,
    progressMessages,
    handleFileSelect,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDeleteUploadedImage,
    handleRemoveUploadingFile,
    removeProgressMessage,
    initForEdit,
    reset,
  };
}

export function useUpdateReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string;
      rating: number;
      content: string;
      title?: string;
      mediaIds?: string[];
    }) => {
      const res = await fetch(`/api/reviews/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error?.message ?? 'Failed to update review');
      }
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REVIEWS_KEY });
    },
  });
}

export function useDeleteReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await fetchReviewsApi<{ deleted: boolean }>(`/${id}`, { method: 'DELETE' });
      return { id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REVIEWS_KEY });
    },
  });
}
